import React, { useState, useEffect } from 'react';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import AppActions from '../../actions/app-actions';
import { preformatWithRequestID } from '../../helpers';

import Loader from '../common/loader';
import UserList from './user-list';
import TenantNameList from './tenant-name-list';
import UserDialog from './user-dialog';

// Pagination not yet supported. A total user count API needs to be implement in backend.

const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [list, setList] = useState([]);
  const [tenantList, setTenantList] = useState([]);
  const [dlg, setDlg] = useState({ open: false });
  const [selectedTenant, setSelectedTenant] = useState(0);

  const showError = (err, msg='') => {
    const { body = {} } = err.res || {};
    const errormsg = body.error ? body.error.charAt(0).toUpperCase() + body.error.slice(1) 
      : 'Please check our connection';
    AppActions.setSnackbar(preformatWithRequestID(err.res, `${msg} ${errormsg}`));    
  };

  const handleCreateUser = () => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (tenantId, userData) => {
      AppActions.createTenantUser(tenantId, userData)
        .then(() => {
          return getUserListByTenantId(tenantList, selectedTenant);
        })
        .then(results => {
          setList(results[0] || []);
          setDlg(() => ({ open: false }));
          AppActions.setSnackbar(`A new user "${userData.email}" created successfully`);
        })
        .catch((err) => {
          showError(err, `Can't create a user ${userData.email}.`);
        });
    };
    setDlg(() => ({ open: true, type: 'create', handleCancel, handleOk, tenantList, selectedTenant: tenantList[selectedTenant] }));
  };

  const getUserListByTenantId = (tenantList, selectedIdx) => {
    return new Promise((resolve, reject) => {
      const tenantId = Array.isArray(tenantList) && tenantList.length > selectedIdx ? tenantList[selectedIdx].id : '';
      if (!tenantId) {
        resolve([]);
      } else {
        AppActions.getTenantUserList(tenantId)
          .then(response => {
            resolve([response.body || [], tenantList]);
          })
          .catch(err => {
            reject(err);
          });        
      }
    });
  };

  useEffect(() => {
    AppActions.getAllTenants()
      .then(response => {
        return getUserListByTenantId(response, 0);
      })
      .then(results => {
        setList(results[0] || []);
        setTenantList(results[1] || []);
      })
      .catch((err) => {
        showError(err, `Users list couldn't be loaded.`);
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {};
  }, []);

  const handleTenantSelected = (idx) => {
    setSelectedTenant(idx);
    setUserLoading(true);
    getUserListByTenantId(tenantList, idx)
      .then(results => {
        setList(results[0] || []);
      })
      .catch(err => {
        setList([]);
        showError(err, `Users list couldn't be loaded.`);
      })
      .finally(() => {
        setUserLoading(false);
      });
  };

  const loader = () => {
    return (
      <div>
        <Loader show={true} />
      </div>        
    );    
  };

  const usersListContent = () => {
    if (!tenantList.length) {
      return (<Typography align='center'>No Users</Typography>);
    } else {
      return (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <TenantNameList list={tenantList} selected={selectedTenant} handleSelected={handleTenantSelected}/>
          </Grid>
          <Grid item xs={8}>
            {
              userLoading
                ? loader()
                : (<UserList list={list} />)
            }
          </Grid>
        </Grid>            
      );
    }
  };

  const bodyContent = () => {
    if (loading) {
      return loader();
    } else {
      return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container justify='flex-end'>
              <Grid item>
                <Button variant='contained' color='primary' onClick={handleCreateUser} disabled={tenantList.length === 0}
                >Create New User</Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {usersListContent()}
          </Grid>
        </Grid>
        {dlg.open ? <UserDialog {...dlg} /> : null}
      </>
      );
    }
  };

  return (
    <>
      {bodyContent()}
    </>
  );
};

export default UserManagement;
